import { Navigate, useRoutes } from 'react-router-dom';
import { Roles, adminRoleNames } from '../assets/data';
import AdminGuard from '../auth/AdminGuard';
import AuthGuard from '../auth/AuthGuard';
import DeveloperGuard from '../auth/DeveloperGuard';
import GuestGuard from '../auth/GuestGuard';
import RoleBasedGuard from '../auth/RoleBasedGuard';
import { PageErrorGuard } from '../components/error-handling/PageErrorGuard';
import { CheckEnvironment, SupportedEnvironments, WritePostLoginPath } from '../config-global';
import { DashboardLayout, CompactLayout } from '../layouts';
import ListingDetailsPage from '../pages/listings/ListingDetailsPage';
import ReservationLandingPage from '../pages/reservations/ReservationLandingPage';
import TestPage from '../pages/dev/TestPage';
import { OwnerReportType } from '../utils/owner-portal/ownerReportConstants';
import {
  BrandSelectorPage,
  CasePage,
  ComingSoonPage,
  DemoNoticesPage,
  ErrorPage,
  FaqsPage,
  DashboardPage,
  ListingListPage,
  LogOutPage,
  LoginPage,
  NotVerifiedPage,
  VerifiedPage,
  MaintenancePage,
  NotificationPage,
  OobActionLandingPage,
  OwnerReportPage,
  Page403,
  Page404,
  Page500,
  PermissionDeniedPage,
  ReservationCalendarPage,
  ReservationCreatePage,
  ReservationEditPage,
  ReservationListPage,
  StatementsPage,
  TimeshareListPage,
  TimeshareDetailsPage,
  TimeshareCreatePage,
  UserAccountPage,
  UserCreatePage,
  UserEditPage,
  UserEmulatorPage,
  UserListPage
} from './elements';
import { OOB_ACTION, OOB_EMAIL_VERIFIED, PATH_USER } from './paths';
import ManagedOwnerCaseRoute from './ManagedOwnerCaseRoute';

// ----------------------------------------------------------------------
//auth/login auth/register
export default function Router() {
  return useRoutes([
    // User
    {
      path: 'user',
      children: [
        { element: <Navigate to={PATH_USER.action} replace />, index: true },
        {
          path: 'login',
          element: (
            <GuestGuard>
              <PageErrorGuard>
                <LoginPage />
              </PageErrorGuard>
            </GuestGuard>
          )
        },
        // ! Disabled self sign up
        // {
        //   path: 'register',
        //   element: (
        //     <GuestGuard>
        //       <RegisterPage />
        //     </GuestGuard>
        //   ),
        // },
        {
          path: 'notifications',
          element: (
            <AuthGuard>
              <RoleBasedGuard hasContent roles={[Roles.User]}>
                <DashboardLayout showNotifications={false} />
              </RoleBasedGuard>
            </AuthGuard>
          ),
          children: [
            {
              index: true,
              element: (
                <PageErrorGuard>
                  {' '}
                  <NotificationPage />{' '}
                </PageErrorGuard>
              )
            }
          ]
        },
        {
          path: 'not-verified',
          element: (
            <AuthGuard>
              <CompactLayout />
            </AuthGuard>
          ),
          children: [{ index: true, element: <NotVerifiedPage /> }]
        },
        // { path: 'login-unprotected', element: <LoginPage /> },
        // { path: 'register-unprotected', element: <RegisterPage /> },
        {
          element: <CompactLayout />,
          children: [
            { path: OOB_ACTION, element: <OobActionLandingPage /> },
            { path: OOB_EMAIL_VERIFIED, element: <VerifiedPage /> }
          ]
        }
      ]
    },

    // Admin: only admin routes (Users); no Dashboard/app, no FAQs under admin
    {
      path: 'admin',
      element: (
        <AuthGuard>
          <AdminGuard>
            <RoleBasedGuard hasContent roles={adminRoleNames}>
              <DashboardLayout showNotifications={false} />
            </RoleBasedGuard>
          </AdminGuard>
        </AuthGuard>
      ),
      children: [
        { element: <Navigate to="/admin/user/list" replace />, index: true },
        {
          path: 'user',
          children: [
            { element: <Navigate to="/admin/user/list" replace />, index: true },
            {
              path: 'list',
              element: (
                <PageErrorGuard>
                  {' '}
                  <UserListPage />{' '}
                </PageErrorGuard>
              )
            },
            {
              path: 'emulator',
              element: (
                <PageErrorGuard>
                  {' '}
                  <UserEmulatorPage />{' '}
                </PageErrorGuard>
              )
            },
            {
              path: 'new',
              element: (
                <PageErrorGuard>
                  {' '}
                  <UserCreatePage />{' '}
                </PageErrorGuard>
              )
            },
            {
              path: ':email/edit',
              element: (
                <PageErrorGuard>
                  {' '}
                  <UserEditPage />{' '}
                </PageErrorGuard>
              )
            },
            {
              path: 'account',
              element: (
                <PageErrorGuard>
                  {' '}
                  <UserAccountPage />{' '}
                </PageErrorGuard>
              )
            }
          ]
        }
      ]
    },
    {
      path: 'faqs',
      element: (
        <AuthGuard>
          <RoleBasedGuard hasContent roles={[Roles.User]}>
            <DashboardLayout showNotifications />
          </RoleBasedGuard>
        </AuthGuard>
      ),
      children: [
        {
          element: (
            <PageErrorGuard>
              {' '}
              <FaqsPage />{' '}
            </PageErrorGuard>
          ),
          index: true
        }
      ]
    },
    {
      path: 'demo',
      element: (
        <AuthGuard>
          <DeveloperGuard>
            <DashboardLayout showNotifications />
          </DeveloperGuard>
        </AuthGuard>
      ),
      children: [
        {
          path: 'notices',
          element: (
            <PageErrorGuard>
              {' '}
              <DemoNoticesPage />{' '}
            </PageErrorGuard>
          )
        }
      ]
    },
    // Dashboard
    {
      path: '/',
      element: (
        <AuthGuard>
          <AdminGuard>
            <DashboardLayout showNotifications={false} />
          </AdminGuard>
        </AuthGuard>
      ),
      children: [
        {
          path: 'booking',
          children: [
            {
              element: (
                <PageErrorGuard>
                  {' '}
                  <ReservationEditPage />{' '}
                </PageErrorGuard>
              ),
              path: 'edit/:id'
            },
            {
              element: (
                <PageErrorGuard>
                  {' '}
                  <ReservationCreatePage />{' '}
                </PageErrorGuard>
              ),
              path: 'create'
            },
            {
              element: (
                <PageErrorGuard>
                  {' '}
                  <ReservationCreatePage />{' '}
                </PageErrorGuard>
              ),
              path: 'create/:id'
            } // pre-set listing Id
          ]
        }
      ]
    },
    {
      path: '/',
      element: (
        <AuthGuard>
          <AdminGuard>
            <DashboardLayout showNotifications />
          </AdminGuard>
        </AuthGuard>
      ),
      children: [
        { element: <Navigate to={WritePostLoginPath(Roles.User)} replace />, index: true },
        {
          path: 'app',
          children: [
            {
              index: true,
              element: (
                <PageErrorGuard>
                  {' '}
                  <DashboardPage />{' '}
                </PageErrorGuard>
              )
            }
          ]
        },
        {
          path: 'calendar',
          element: (
            <PageErrorGuard>
              {' '}
              <ReservationCalendarPage />{' '}
            </PageErrorGuard>
          )
        },
        {
          path: 'booking',
          children: [
            {
              element: (
                <PageErrorGuard>
                  {' '}
                  <ReservationListPage />{' '}
                </PageErrorGuard>
              ),
              index: true
            },
            {
              element: (
                <PageErrorGuard>
                  {' '}
                  <ReservationLandingPage />{' '}
                </PageErrorGuard>
              ),
              path: ':id'
            }
          ]
        },
        {
          path: 'timeshare',
          children: [
            {
              index: true,
              element: (
                <PageErrorGuard>
                  <TimeshareListPage />
                </PageErrorGuard>
              )
            },
            {
              path: 'new',
              element: (
                <PageErrorGuard>
                  <TimeshareCreatePage />
                </PageErrorGuard>
              )
            },
            {
              path: 'renew/:id',
              element: (
                <PageErrorGuard>
                  <TimeshareCreatePage />
                </PageErrorGuard>
              )
            },
            {
              path: ':id',
              element: (
                <PageErrorGuard>
                  <TimeshareDetailsPage />
                </PageErrorGuard>
              )
            }
          ]
        },
        {
          path: 'statements',
          element: (
            <PageErrorGuard>
              {' '}
              <StatementsPage />{' '}
            </PageErrorGuard>
          )
        },
        {
          path: 'case',
          element: (
            <PageErrorGuard>
              <ManagedOwnerCaseRoute>
                <CasePage />
              </ManagedOwnerCaseRoute>
            </PageErrorGuard>
          )
        },
        {
          path: 'reports',
          children: [
            { element: <Navigate to="/dashboard/reports" replace />, index: true },
            {
              path: 'occupancy',
              element: (
                <PageErrorGuard>
                  {' '}
                  <OwnerReportPage reportType={OwnerReportType.Occupancy} />{' '}
                </PageErrorGuard>
              )
            },
            {
              path: 'revenue',
              element: (
                <PageErrorGuard>
                  {' '}
                  <OwnerReportPage reportType={OwnerReportType.Revenue} />{' '}
                </PageErrorGuard>
              )
            },
            {
              path: 'bookings',
              element: (
                <PageErrorGuard>
                  {' '}
                  <OwnerReportPage reportType={OwnerReportType.Booking} />{' '}
                </PageErrorGuard>
              )
            }
          ]
        },
        {
          path: 'user',
          children: [
            { element: <Navigate to="/dashboard/user/profile" replace />, index: true },
            {
              path: 'account',
              element: (
                <PageErrorGuard>
                  {' '}
                  <UserAccountPage />{' '}
                </PageErrorGuard>
              )
            }
          ]
        },
        {
          path: 'property',
          children: [
            {
              index: true,
              element: (
                <PageErrorGuard>
                  {' '}
                  <ListingListPage />{' '}
                </PageErrorGuard>
              )
            },
            {
              element: (
                <PageErrorGuard>
                  {' '}
                  <ListingDetailsPage />{' '}
                </PageErrorGuard>
              ),
              path: ':id'
            }
          ]
        },
        {
          path: 'test',
          element: CheckEnvironment(SupportedEnvironments.Dev) ? (
            <PageErrorGuard>
              {' '}
              <TestPage unused={undefined as any} />{' '}
            </PageErrorGuard>
          ) : (
            <Navigate to="/404" replace />
          )
        }, // dev only route
        { path: 'permission-denied', element: <PermissionDeniedPage /> }
      ]
    },
    // Development routes
    {
      path: 'dev/brand',
      element: CheckEnvironment(SupportedEnvironments.Dev) ? (
        <PageErrorGuard>
          {' '}
          <BrandSelectorPage />{' '}
        </PageErrorGuard>
      ) : (
        <Navigate to="/404" replace />
      )
    },
    {
      element: <CompactLayout />,
      children: [
        { path: 'coming-soon', element: <ComingSoonPage /> },
        { path: 'maintenance', element: <MaintenancePage /> },
        { path: '500', element: <Page500 /> },
        { path: '404', element: <Page404 /> },
        { path: '403', element: <Page403 /> },
        { path: 'error', element: <ErrorPage /> },
        { path: 'logout', element: <LogOutPage /> }
      ]
    },
    { path: '*', element: <Navigate to="/404" replace /> }
  ]);
}
